import { useId, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from "react";
import { describeRule, validateImageFile, type ImageRule } from "@/utils/imageValidation";
import { releasePreviewUrl, uploadImage, type UploadFolder } from "@/services/imageUploadService";

interface ImageUploaderProps {
  /** Current image URLs (0..n). */
  value: string[];
  /** Called with the next full list of URLs after an add or remove. */
  onChange: (urls: string[]) => void;
  /** Validation preset applied to every file. */
  rule: ImageRule;
  /** Logical destination folder on the backend. */
  folder: UploadFolder;
  /** Bearer token for authenticated uploads. */
  token?: string | null;
  /** Allow more than one image (gallery mode). */
  multiple?: boolean;
  /** Hard cap when multiple is true. */
  maxFiles?: number;
  /** Extra classes for the preview thumbnail. */
  previewClassName?: string;
  /** Renders the preview as a wide banner instead of a square tile. */
  variant?: "tile" | "banner";
  /** When provided, shows a “Choose from Media Library” action. */
  onBrowseLibrary?: () => void;
  /** Label for the library action (defaults to “Choose from library”). */
  browseLabel?: string;
  /**
   * Optional message shown in place of the dropzone once the uploader is full
   * (multiple mode only). Use it to explain the limit or point at an upgrade.
   */
  capacityMessage?: ReactNode;
}

const MAX_ERRORS_SHOWN = 4;

/**
 * Accessible, dependency-free image uploader.
 * Validates file type, MIME, size and dimensions before uploading, shows inline
 * feedback and supports click-to-pick plus drag & drop.
 */
export default function ImageUploader({
  value,
  onChange,
  rule,
  folder,
  token = null,
  multiple = false,
  maxFiles = 8,
  previewClassName = "",
  variant = "tile",
  onBrowseLibrary,
  browseLabel = "Choose from library",
  capacityMessage,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [errors, setErrors] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const atCapacity = multiple ? value.length >= maxFiles : value.length >= 1;
  const busy = uploading;

  async function handleFiles(fileList: FileList | File[]) {
    const incoming = Array.from(fileList);
    if (incoming.length === 0) return;

    const collected: string[] = [];
    const accepted: File[] = [];

    let queue = incoming;
    if (!multiple) {
      queue = incoming.slice(0, 1);
      if (incoming.length > 1) collected.push("Only one image is allowed here.");
    } else {
      const slots = Math.max(0, maxFiles - value.length);
      if (incoming.length > slots) {
        collected.push(`You can add ${maxFiles} images — only ${slots} more could be accepted.`);
      }
      queue = incoming.slice(0, slots);
    }

    for (const file of queue) {
      const result = await validateImageFile(file, rule);
      if (result.ok) {
        accepted.push(file);
      } else {
        collected.push(`${file.name}: ${result.errors.join(" ")}`);
      }
    }

    setErrors(collected);
    if (accepted.length === 0) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const uploaded = await Promise.all(accepted.map((file) => uploadImage(file, folder, { token })));
      onChange(multiple ? [...value, ...uploaded] : [uploaded[uploaded.length - 1]]);
      setErrors([]);
    } catch (err) {
      setErrors([err instanceof Error ? err.message : "Upload failed. Please try again."]);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function onInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files) void handleFiles(event.target.files);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (busy || atCapacity) return;
    if (event.dataTransfer?.files) void handleFiles(event.dataTransfer.files);
  }

  function onDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (!busy && !atCapacity) setDragging(true);
  }

  function removeAt(index: number) {
    const target = value[index];
    if (target) releasePreviewUrl(target);
    onChange(value.filter((_, i) => i !== index));
  }

  const acceptAttr = rule.allowedExtensions.map((ext) => `.${ext}`).join(",");
  const bannerPreview = variant === "banner";
  const showDropzone = !atCapacity;

  return (
    <div className="flex flex-col gap-2.5">
      {value.length > 0 && (
        <div className={multiple ? "grid grid-cols-2 gap-2.5 sm:grid-cols-3" : ""}>
          {value.map((url, index) => (
            <div
              key={`${url}-${index}`}
              className={`group relative overflow-hidden rounded-lg border border-background-200 bg-background-100 ${
                bannerPreview ? "h-24 w-full" : "h-28 w-full"
              } ${previewClassName}`}
            >
              <img src={url} alt={`${rule.label} preview ${index + 1}`} title={rule.label} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(index)}
                title="Remove image"
                className="absolute right-1.5 top-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-foreground-950/60 text-background-50 opacity-0 transition-opacity hover:bg-foreground-950/80 focus:opacity-100 group-hover:opacity-100"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showDropzone && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => !busy && inputRef.current?.click()}
          onKeyDown={(event) => {
            if ((event.key === "Enter" || event.key === " ") && !busy) inputRef.current?.click();
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={() => setDragging(false)}
          className={`flex w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-5 text-center transition-colors ${
            dragging ? "border-primary-400 bg-primary-50" : "border-background-300 bg-background-100/60 hover:border-primary-300 hover:bg-background-100"
          } ${busy ? "cursor-wait opacity-70" : ""}`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 text-primary-600">
            <i className={busy ? "ri-loader-4-line animate-spin text-lg" : "ri-upload-cloud-2-line text-lg"} />
          </span>
          <p className="text-xs font-semibold text-foreground-800">
            {busy ? "Uploading…" : `Add ${rule.label.toLowerCase()}`}
          </p>
          <p className="text-[11px] text-foreground-500">{describeRule(rule)}</p>
          <p className="text-[11px] text-foreground-400">Click to browse or drag a file here</p>
        </div>
      )}

      {onBrowseLibrary && (
        <button
          type="button"
          onClick={onBrowseLibrary}
          className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-image-2-line text-sm" />
          </span>
          {browseLabel}
        </button>
      )}

      {atCapacity && multiple && (
        <p className="flex items-start gap-1.5 rounded-lg border border-background-200 bg-background-100/70 px-3 py-2 text-[11px] leading-relaxed text-foreground-600">
          <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-primary-600">
            <i className="ri-information-line text-sm" />
          </span>
          <span>
            {capacityMessage ?? `You have added the maximum of ${maxFiles} images. Remove one to add another.`}
          </span>
        </p>
      )}

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        multiple={multiple}
        className="hidden"
        onChange={onInputChange}
      />

      {errors.length > 0 && (
        <ul className="flex flex-col gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
          {errors.slice(0, MAX_ERRORS_SHOWN).map((message) => (
            <li key={message} className="flex items-start gap-1.5 text-[11px] text-rose-700">
              <i className="ri-error-warning-line mt-0.5 shrink-0" />
              <span>{message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}